import { useState, useEffect } from 'react';
import { enquiryController } from '../../controllers/enquiryController';
import Enquiry from '../../models/Enquiry';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown, CheckCircle, UserPlus } from 'lucide-react';

const ConvertedEnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '', email: '', phone: '', company: '', subject: '',
    message: '', enquiryType: 'General', source: 'Website', status: 'Converted',
    priority: 'Medium', estimatedValue: 0, followUpDate: '', notes: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchEnquiries(); }, []);

  useEffect(() => {
    let result = [...enquiries];
    if (searchQuery) {
      result = result.filter(e => 
        e.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.enquiryId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (filterPriority !== 'All') result = result.filter(e => e.priority === filterPriority);
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setFilteredEnquiries(result);
    setCurrentPage(1);
  }, [enquiries, searchQuery, filterPriority, sortField, sortDirection]);

  const fetchEnquiries = async () => {
    setLoading(true);
    // Only fetch enquiries with "Converted" status
    const result = await enquiryController.getAll({ status: 'Converted' });
    if (result.success) setEnquiries(result.data);
    else alert('Error: ' + result.message);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({ customerName: '', email: '', phone: '', company: '', subject: '', message: '', enquiryType: 'General', source: 'Website', status: 'Converted', priority: 'Medium', estimatedValue: 0, followUpDate: '', notes: '' });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await enquiryController.create(formData);
    if (result.success) { setShowAddModal(false); fetchEnquiries(); alert('✅ Enquiry created!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleEdit = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormData({ ...enquiry, followUpDate: enquiry.followUpDate?.split('T')[0] || '' });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    const result = await enquiryController.update(selectedEnquiry.id, formData);
    if (result.success) { setShowEditModal(false); fetchEnquiries(); alert('✅ Enquiry updated!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleView = (enquiry) => { setSelectedEnquiry(enquiry); setShowViewModal(true); };

  const handleDelete = async (enquiry) => {
    try {
      const isConfirmed = window.confirm(
        `Delete enquiry from ${enquiry.customerName}?`
      );
  
      if (!isConfirmed) return;
  
      const result = await enquiryController.delete(enquiry.id || enquiry._id);
  
      if (result?.success) {
        await fetchEnquiries();
        alert('✅ Enquiry deleted!');
      } else {
        alert(result?.message || '❌ Failed to delete enquiry');
      }
    } catch (error) {
      console.error('Delete enquiry error:', error);
      alert('❌ Something went wrong while deleting');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEnquiries = filteredEnquiries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);

  const stats = {
    total: enquiries.length,
    high: enquiries.filter(e => e.priority === 'High').length,
    medium: enquiries.filter(e => e.priority === 'Medium').length,
    low: enquiries.filter(e => e.priority === 'Low').length
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '100px' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Converted Enquiries</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }}><Download size={18} /> Export</button>
          <button style={styles.button} onClick={handleAdd}><Plus size={18} /> Add Enquiry</button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}><div style={styles.statLabel}>Total Converted</div><div style={{ ...styles.statValue, color: '#10b981' }}>{stats.total}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>High Priority</div><div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.high}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Medium Priority</div><div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.medium}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Low Priority</div><div style={{ ...styles.statValue, color: '#10b981' }}>{stats.low}</div></div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input type="text" placeholder="Search enquiries..." style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select style={styles.select} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="All">All Priority</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : currentEnquiries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No converted enquiries found.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('enquiryId')}>ID</th>
                  <th style={styles.th} onClick={() => handleSort('customerName')}>Customer</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Subject</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Value</th>
                  <th style={styles.th}>Days</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentEnquiries.map((enq) => (
                  <tr key={enq.id}>
                    <td style={styles.td}><div style={{ fontWeight: '500', fontSize: '12px', color: '#64748b' }}>{enq.enquiryId}</div></td>
                    <td style={styles.td} onClick={() => handleView(enq)}><div style={{ fontWeight: '500', cursor: 'pointer' }}>{enq.customerName}</div><div style={{ fontSize: '12px', color: '#64748b' }}>{enq.company || 'N/A'}</div></td>
                    <td style={styles.td}><div style={{ fontSize: '13px' }}>{enq.email}</div><div style={{ fontSize: '12px', color: '#64748b' }}>{enq.phone}</div></td>
                    <td style={styles.td}>{enq.subject}</td>
                    <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: `${enq.getSourceColor()}20`, color: enq.getSourceColor() }}>{enq.source}</span></td>
                    <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: `${enq.getPriorityColor()}20`, color: enq.getPriorityColor() }}>{enq.priority}</span></td>
                    <td style={styles.td}><div style={{ fontWeight: '500' }}>₹{enq.estimatedValue.toLocaleString()}</div></td>
                    <td style={styles.td}>{enq.getDaysPending()}d</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={() => handleView(enq)} title="View"><Eye size={18} color="#64748b" /></button>
                        <button style={styles.actionButton} onClick={() => handleEdit(enq)} title="Edit"><Edit2 size={18} color="#3b82f6" /></button>
                        <button style={styles.actionButton} onClick={() => handleDelete(enq)} title="Delete"><Trash2 size={18} color="#ef4444" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredEnquiries.length)} of {filteredEnquiries.length}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center' }}>Page {currentPage} of {totalPages}</div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add Enquiry' : 'Edit Enquiry'}</h2>
              <button style={styles.actionButton} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}><X size={20} /></button>
            </div>
            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (<div style={styles.errorList}>{formErrors.map((err, i) => <div key={i} style={styles.errorItem}><AlertCircle size={14} style={{ display: 'inline', marginRight: '8px' }} />{err}</div>)}</div>)}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}><label style={styles.label}>Customer Name *</label><input type="text" name="customerName" style={styles.input} value={formData.customerName} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Email *</label><input type="email" name="email" style={styles.input} value={formData.email} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Phone *</label><input type="tel" name="phone" style={styles.input} value={formData.phone} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Company</label><input type="text" name="company" style={styles.input} value={formData.company} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Enquiry Type</label><select name="enquiryType" style={styles.input} value={formData.enquiryType} onChange={handleInputChange}><option value="General">General</option><option value="Product">Product</option><option value="Service">Service</option><option value="Support">Support</option><option value="Quote">Quote</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Source</label><select name="source" style={styles.input} value={formData.source} onChange={handleInputChange}><option value="Website">Website</option><option value="Email">Email</option><option value="Phone">Phone</option><option value="Social Media">Social Media</option><option value="Referral">Referral</option><option value="Walk-in">Walk-in</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Status</label><select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}><option value="Pending">Pending</option><option value="In Progress">In Progress</option><option value="Follow-up">Follow-up</option><option value="Converted">Converted</option><option value="Closed">Closed</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Priority</label><select name="priority" style={styles.input} value={formData.priority} onChange={handleInputChange}><option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Estimated Value (₹)</label><input type="number" name="estimatedValue" style={styles.input} value={formData.estimatedValue} onChange={handleInputChange} min="0" /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Follow-up Date</label><input type="date" name="followUpDate" style={styles.input} value={formData.followUpDate} onChange={handleInputChange} /></div>
                </div>
                <div style={styles.formGroup}><label style={styles.label}>Subject *</label><input type="text" name="subject" style={styles.input} value={formData.subject} onChange={handleInputChange} required /></div>
                <div style={styles.formGroup}><label style={styles.label}>Message *</label><textarea name="message" style={styles.textarea} value={formData.message} onChange={handleInputChange} required /></div>
                <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea name="notes" style={styles.textarea} value={formData.notes} onChange={handleInputChange} /></div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>Cancel</button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>{formSubmitting ? 'Saving...' : (showAddModal ? 'Create' : 'Update')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedEnquiry && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Enquiry Details</h2>
              <button style={styles.actionButton} onClick={() => setShowViewModal(false)}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Enquiry ID</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.enquiryId}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Customer Name</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.customerName}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.email}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Phone</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.phone}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Company</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.company || 'N/A'}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Source</div><span style={{ ...styles.badge, backgroundColor: `${selectedEnquiry.getSourceColor()}20`, color: selectedEnquiry.getSourceColor() }}>{selectedEnquiry.source}</span></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div><span style={{ ...styles.badge, backgroundColor: '#10b98120', color: '#10b981' }}>Converted</span></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Priority</div><span style={{ ...styles.badge, backgroundColor: `${selectedEnquiry.getPriorityColor()}20`, color: selectedEnquiry.getPriorityColor() }}>{selectedEnquiry.priority}</span></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Estimated Value</div><div style={{ fontSize: '16px', fontWeight: '500' }}>₹{selectedEnquiry.estimatedValue.toLocaleString()}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Days Since Creation</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.getDaysPending()} days</div></div>
              </div>
              <div style={{ marginTop: '24px' }}><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Subject</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selectedEnquiry.subject}</div></div>
              <div style={{ marginTop: '24px' }}><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Message</div><div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '14px' }}>{selectedEnquiry.message}</div></div>
              {selectedEnquiry.notes && (<div style={{ marginTop: '24px' }}><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Notes</div><div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '14px' }}>{selectedEnquiry.notes}</div></div>)}
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedEnquiry); }}><Edit2 size={16} /> Edit</button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvertedEnquiriesPage;